/**
 * <cart-drawer> — keeps the bag drawer in step with the cart on the server.
 *
 * Quantity steps post to Shopify's cart API, then the section is re-rendered through the Section
 * Rendering API and swapped in, so what the shopper sees is always the server's cart rather than a
 * client-side guess. Each line's Remove button asks for 0, which removes the line; the minus is
 * disabled at a quantity of 1 (aria-disabled, with no target), so Remove is the only way out. Every
 * control carries its target quantity in data-quantity-change. Every other part of the theme talks
 * to it through events:
 *   - `cart:added` (dispatched after an add to cart) refreshes the drawer and opens it
 *   - `cart:updated` is dispatched after any change, carrying the new cart, so the header count
 *     and anything else interested can update.
 */
class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.section;
    this.addEventListener('click', this.onClick);
    if (!CartDrawer.listening) {
      document.addEventListener('cart:added', (event) => {
        const drawer = CartDrawer.current;
        if (!drawer) return;
        if (!drawer.render(event.detail?.sections)) drawer.refresh({ open: true });
        else drawer.closest('site-drawer')?.show();
      });
      CartDrawer.listening = true;
    }
    CartDrawer.current = this;
  }

  static listening = false;
  static current = null;

  onClick = (event) => {
    const button = event.target.closest('[data-quantity-change]');
    if (!button) return;
    event.preventDefault();
    const line = button.closest('[data-line-key]');
    if (!line) return;
    this.change(line.dataset.lineKey, Number(button.dataset.quantityChange), button.dataset.cartControl);
  };

  /**
   * Post a line's new quantity and swap in the re-rendered drawer. `control` names the control
   * that asked (decrease, increase or remove): if focus was in the drawer it goes back to that
   * control on the same line, or to the drawer's title when the line is gone.
   */
  async change(key, quantity, control) {
    const hadFocus = this.contains(document.activeElement);
    this.classList.add('cart-drawer--busy');
    try {
      const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: key,
          quantity,
          // ask for the re-rendered drawer in the same request, so the markup and the cart
          // it describes always come from the same moment on the server
          sections: this.sectionId,
          sections_url: window.location.pathname,
        }),
      });
      const cart = await response.json();
      if (cart.sections) this.render(cart.sections);
      else await this.refresh();
      // the control that was pressed has just been replaced: keep keyboard and screen-reader
      // focus where it was, on the same control of the same line, or on the title if it is gone
      if (hadFocus) this.restoreFocus(key, control);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
    } finally {
      this.classList.remove('cart-drawer--busy');
    }
  }

  restoreFocus(key, control) {
    const line = [...this.querySelectorAll('[data-line-key]')].find((el) => el.dataset.lineKey === key);
    const target = control ? line?.querySelector(`[data-cart-control="${control}"]`) : null;
    (target ?? this.querySelector('[data-drawer-focus]'))?.focus();
  }

  /** Swap in a rendered copy of this section from a cart response. */
  render(sections) {
    const markup = sections?.[this.sectionId];
    if (!markup) return false;
    const parsed = new DOMParser().parseFromString(markup, 'text/html');
    const next = parsed.querySelector('cart-drawer');
    if (next) {
      this.innerHTML = next.innerHTML;
      this.dataset.itemCount = next.dataset.itemCount ?? '';
      this.dataset.countLabel = next.dataset.countLabel ?? '';
      this.syncCount(next);
    }
    return true;
  }

  /** Replace the drawer's contents with a freshly rendered copy of this section. */
  async refresh({ open = false } = {}) {
    const url = `${window.location.pathname}?sections=${this.sectionId}`;
    // no-store: a cached copy of this response would show a stale cart
    const response = await fetch(url, { headers: { Accept: 'application/json' }, cache: 'no-store' });
    const sections = await response.json();
    const markup = sections[this.sectionId];
    this.render(sections);
    if (open) this.closest('site-drawer')?.show();
  }

  /**
   * Mirror the item count from the re-rendered drawer into the header: the BAG [n] figure and the
   * label read out for it, which the server rendered for the page's original cart and would
   * otherwise keep saying the old count after an add.
   */
  syncCount(drawer) {
    const { itemCount, countLabel } = drawer.dataset;
    if (!itemCount) return;
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = itemCount;
      if (countLabel) el.closest('.header__cart-count')?.setAttribute('aria-label', countLabel);
    });
  }
}

if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);
